import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    LuChevronLeft,
    LuChevronDown,
    LuChevronUp,
    LuPlus,
    LuTrash2,
    LuArrowUp,
    LuArrowDown,
    LuSparkles,
    LuCheck,
} from 'react-icons/lu';

import { QuizzesAPI } from '~/services/db/quizzes';
import { logEvent } from '~/config/firebase.config';

import cockpit from '../Animator.module.css';
import styles from './QuizStudio.module.css';
import {
    QUIZ_CATEGORIES,
    StudioQuestion,
    StudioQuiz,
    emptyQuestion,
    emptyQuiz,
    firstQuizIssue,
    QuizIssue,
    questionIsComplete,
    quizIssueMessage,
    quizIsPlayable,
    studioQuizFromDoc,
} from './quizTypes';
import {
    deleteDraft,
    draftHasContent,
    draftIsStale,
    editableFingerprint,
    getDraft,
    saveDraft,
    timeMs,
} from './quizDraft';

/** Recopie du brouillon après une pause de frappe. */
const DRAFT_DEBOUNCE_MS = 400;

const OPTION_LETTERS = ['A', 'B', 'C', 'D'];
const DURATIONS = [15, 20, 30];
/** Mode de jeu d'une question dans le Plateau (absent = automatique). */
const MODES = [
    [undefined, 'Automatique'],
    ['none', 'Classique'],
    ['eclair', '⚡ Éclair'],
    ['mise', '💰 La Mise'],
] as const;

/* Au-delà de ce poids, l'image est réduite avant upload : une photo de
 * téléphone pleine résolution pèse plusieurs Mo, serait stockée telle quelle
 * et re-téléchargée par chaque TV à chaque partie. Sous le seuil (logos PNG
 * transparents inclus), le fichier part tel quel. */
const RESIZE_THRESHOLD_BYTES = 300_000;
const MAX_IMAGE_DIM = 1600;

/** Lit le fichier en data URL, réduit à 1600px/JPEG s'il est lourd. */
function fileToUploadDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const readRaw = () => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
            reader.readAsDataURL(file);
        };
        if (file.size <= RESIZE_THRESHOLD_BYTES) {
            readRaw();
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            const scale = Math.min(1, MAX_IMAGE_DIM / Math.max(img.width, img.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                readRaw();
                return;
            }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            readRaw(); // format non décodable en <img> : tel quel
        };
        img.src = objectUrl;
    });
}

/** Prépare une question au changement de type (options / bonnes réponses cohérentes). */
function coerceType(q: StudioQuestion, type: StudioQuestion['type']): StudioQuestion {
    if (type === 'vf') {
        return { ...q, type, options: ['Vrai', 'Faux'], correctAnswer: 0, correctAnswers: undefined };
    }
    const options = q.options.length === 4 ? q.options : ['', '', '', ''].map((v, i) => q.options[i] ?? v);
    if (type === 'qcm') {
        const seed = q.correctAnswer !== undefined ? [q.correctAnswer] : [];
        return {
            ...q,
            type,
            // Éclair = un seul joueur répond : incompatible avec plusieurs réponses.
            mechanic: q.mechanic === 'eclair' ? undefined : q.mechanic,
            options,
            correctAnswers: q.correctAnswers?.length ? q.correctAnswers : seed,
            correctAnswer: undefined,
        };
    }
    return {
        ...q,
        type,
        options,
        correctAnswer: q.correctAnswer ?? q.correctAnswers?.[0] ?? 0,
        correctAnswers: undefined,
    };
}

/**
 * L'éditeur complet du studio : méta du quiz + questions dépliables, pensé
 * pour marcher au pouce sur téléphone comme au clavier sur ordinateur.
 * Sert aussi de RELECTURE aux quiz générés par l'IA (state.draft + fromAI).
 */
export function QuizEditor() {
    const navigate = useNavigate();
    const location = useLocation();
    const { clientID, quizId } = useParams();
    const isNew = !quizId || quizId === 'new';
    // `restored` = ouvert depuis le bandeau « Brouillon non enregistré » de Mes quiz.
    const draftState = (location.state ?? {}) as {
        draft?: StudioQuiz;
        fromAI?: boolean;
        restored?: boolean;
        justSaved?: boolean;
    };

    // Le brouillon stocké passe avant celui de l'historique (`location.state` survit à un
    // rechargement et au bouton Retour : il peut être plus ancien que le stockage).
    const [quiz, setQuiz] = useState<StudioQuiz | null>(() => {
        if (!isNew) return null;
        const stored = draftState.draft && clientID ? getDraft(clientID, draftState.draft.id) : null;
        return stored?.quiz ?? draftState.draft ?? emptyQuiz();
    });
    const [restored, setRestored] = useState(isNew && !!draftState.restored);
    // Version enregistrée quand on a rouvert un brouillon, et « elle a changé ailleurs depuis »
    // (on prévient, on n'efface jamais un brouillon tout seul).
    const [savedVersion, setSavedVersion] = useState<StudioQuiz | null>(null);
    const [outdated, setOutdated] = useState(false);
    const [saveError, setSaveError] = useState(false);
    // Dernière écriture du brouillon réussie ? (navigation privée, stockage plein)
    const [draftStored, setDraftStored] = useState(true);
    // Date de la version enregistrée d'où part le brouillon (null = jamais enregistré / inconnue).
    const baseUpdatedAtRef = useRef<number | null>(null);
    const [openId, setOpenId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    // Slot en cours d'upload ('cover' ou id de question), + slot en erreur
    // (le message s'affiche À CÔTÉ du bloc concerné, pas en haut du formulaire).
    const [uploading, setUploading] = useState<string | null>(null);
    const [uploadErrorSlot, setUploadErrorSlot] = useState<string | null>(null);

    // Brouillon : chaque modification est recopiée dans ce navigateur, effacée à
    // l'enregistrement confirmé. `baseRef` = version enregistrée (null = rien
    // d'enregistré à quoi comparer) ; revenir à l'identique retire le brouillon.
    const baseRef = useRef<string | null>(isNew && !draftState.draft && quiz ? editableFingerprint(quiz) : null);
    // Dernier contenu écrit : on ne réécrit pas à l'identique.
    const lastWrittenRef = useRef<string | null>(isNew && draftState.draft && quiz ? editableFingerprint(quiz) : null);
    const quizRef = useRef(quiz);
    quizRef.current = quiz;
    const goToListTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Faux pendant le démontage : ne pas renaviguer vers l'éditeur qu'on quitte.
    const mountedRef = useRef(true);
    // Un quiz relu après l'IA est déjà dans Firestore : son brouillon rouvre la page d'édition.
    const draftIsNew = isNew && !draftState.fromAI;
    const flushDraftRef = useRef(() => {});
    flushDraftRef.current = () => {
        const { current } = quizRef;
        if (!clientID || !current) return;
        const fp = editableFingerprint(current);
        if (baseRef.current !== null && fp === baseRef.current) {
            if (lastWrittenRef.current !== null) deleteDraft(clientID, current.id);
            lastWrittenRef.current = null;
            return;
        }
        if (fp === lastWrittenRef.current || !draftHasContent(current)) return;
        const ok = saveDraft(clientID, current, draftIsNew, baseUpdatedAtRef.current);
        setDraftStored(ok);
        if (!ok) return;
        const firstWrite = lastWrittenRef.current === null;
        lastWrittenRef.current = fp;
        // Nouveau quiz : l'historique retient ce brouillon → un rechargement le rouvre.
        if (firstWrite && isNew && !draftState.draft && mountedRef.current) {
            navigate('.', { replace: true, state: { ...draftState, draft: current, restored: true } });
        }
    };
    useEffect(() => {
        const timer = setTimeout(() => flushDraftRef.current(), DRAFT_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [quiz]);
    // Onglet fermé / page rechargée / éditeur quitté : on écrit tout de suite.
    useEffect(() => {
        const onHide = () => flushDraftRef.current();
        window.addEventListener('pagehide', onHide);
        window.addEventListener('beforeunload', onHide);
        return () => {
            window.removeEventListener('pagehide', onHide);
            window.removeEventListener('beforeunload', onHide);
            mountedRef.current = false;
            onHide();
            if (goToListTimerRef.current) clearTimeout(goToListTimerRef.current);
        };
    }, []);

    // Nouveau quiz vierge : la première question s'ouvre d'elle-même.
    useEffect(() => {
        if (isNew && !draftState.fromAI && quiz) setOpenId(quiz.questions[0]?.id ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Copie de brouillon venue de l'historique sans brouillon stocké : si ce quiz existe déjà
    // dans Firestore (enregistré depuis), on ouvre la version enregistrée au lieu de la vieille copie.
    useEffect(() => {
        if (!isNew || !clientID || !draftState.draft || getDraft(clientID, draftState.draft.id)) return undefined;
        let alive = true;
        const { id } = draftState.draft;
        QuizzesAPI.retrieveQuiz(clientID, 'unquiz', id).then((data) => {
            if (alive && data) navigate(`/animator/${clientID}/quizzes/${id}`, { replace: true });
        });
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Édition d'un quiz existant : chargement Firestore
    useEffect(() => {
        if (isNew || !clientID || !quizId) return undefined;
        // Juste après l'enregistrement d'un nouveau quiz (page remplacée par la sienne) : le contenu
        // est déjà là, le recharger écraserait ce qui est tapé pendant le retour à la liste.
        if (draftState.justSaved && quizRef.current?.id === quizId) return undefined;
        let alive = true;
        QuizzesAPI.retrieveQuiz(clientID, 'unquiz', quizId).then((data) => {
            if (!alive) return;
            const draft = getDraft(clientID, quizId);
            if (!data) {
                // Quiz supprimé ailleurs mais brouillon gardé ici : on le rouvre (l'enregistrer le recrée).
                if (draft) {
                    baseRef.current = null;
                    lastWrittenRef.current = editableFingerprint(draft.quiz);
                    setRestored(true);
                    setQuiz(draft.quiz);
                } else navigate(`/animator/${clientID}/quizzes`);
                return;
            }
            const loaded = studioQuizFromDoc(data);
            baseRef.current = editableFingerprint(loaded);
            baseUpdatedAtRef.current = timeMs(data.updatedAt);
            // Un brouillon non enregistré de CE quiz passe avant la version enregistrée.
            if (draft) {
                // Le brouillon garde sa propre date de départ (ne pas la remplacer par celle d'aujourd'hui).
                baseUpdatedAtRef.current = draft.baseUpdatedAt ?? null;
                lastWrittenRef.current = editableFingerprint(draft.quiz);
                setSavedVersion(loaded);
                setOutdated(draftIsStale(draft, data.updatedAt));
            }
            setRestored(!!draft);
            setQuiz(draft ? draft.quiz : loaded);
        });
        return () => {
            alive = false;
        };
    }, [isNew, clientID, quizId, navigate]);

    if (!quiz) {
        return (
            <div className={cockpit.page}>
                <div className={cockpit.shell}>
                    <p className={cockpit.muted}>Chargement du quiz…</p>
                </div>
            </div>
        );
    }

    const patchQuiz = (patch: Partial<StudioQuiz>) => {
        setSaved(false);
        setSaveError(false);
        setQuiz((prev) => (prev ? { ...prev, ...patch } : prev));
    };

    const patchQuestion = (id: string, patch: Partial<StudioQuestion>) => {
        setSaved(false);
        setSaveError(false);
        setQuiz((prev) =>
            prev ? { ...prev, questions: prev.questions.map((q) => (q.id === id ? { ...q, ...patch } : q)) } : prev
        );
    };

    const moveQuestion = (index: number, delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= quiz.questions.length) return;
        const questions = quiz.questions.slice();
        [questions[index], questions[target]] = [questions[target], questions[index]];
        patchQuiz({ questions });
    };

    const removeQuestion = (id: string) => {
        patchQuiz({ questions: quiz.questions.filter((q) => q.id !== id) });
    };

    const addQuestion = () => {
        const q = emptyQuestion();
        patchQuiz({ questions: [...quiz.questions, q] });
        setOpenId(q.id);
    };

    /** Upload direct (le studio vit DANS la console → accès Firebase Storage). */
    const uploadImage = async (slot: string, folder: string, file: File, apply: (url: string) => void) => {
        if (!clientID || !quiz || uploading) return;
        setUploading(slot);
        setUploadErrorSlot(null);
        try {
            const dataUrl = await fileToUploadDataUrl(file);
            const { url } = await QuizzesAPI.uploadImageFromDataUrl({
                clientID,
                gameID: 'unquiz',
                quizId: quiz.id,
                folder,
                fileName: file.name,
                dataUrl,
            });
            setSaved(false);
            apply(url);
        } catch (e: any) {
            console.error('Quiz image upload failed:', e);
            setUploadErrorSlot(slot);
        } finally {
            setUploading(null);
        }
    };

    const toggleCorrect = (q: StudioQuestion, index: number) => {
        if (q.type === 'qcm') {
            const current = q.correctAnswers ?? [];
            const next = current.includes(index) ? current.filter((i) => i !== index) : [...current, index];
            patchQuestion(q.id, { correctAnswers: next });
        } else {
            patchQuestion(q.id, { correctAnswer: index });
        }
    };

    const save = async () => {
        if (!clientID || saving || !quizIsPlayable(quiz)) return;
        setSaving(true);
        setSaveError(false);
        // Le brouillon est écrit AVANT l'appel : rien ne se perd si Firestore échoue.
        flushDraftRef.current();
        logEvent('studio_save_quiz', clientID, {
            clientID,
            quizId: quiz.id,
            questions: quiz.questions.length,
            fromAI: !!draftState.fromAI,
        });
        try {
            // null = « champ retiré » : saveQuiz le convertit en deleteField()
            // (un champ simplement absent laisserait l'ancienne valeur en base).
            await QuizzesAPI.saveQuiz(clientID, 'unquiz', quiz.id, {
                title: quiz.title.trim(),
                description: quiz.description?.trim() || null,
                category: quiz.category || null,
                coverImage: quiz.coverImage || null,
                questions: quiz.questions,
                scoringType: quiz.scoringType,
                lossEnabled: quiz.lossEnabled,
                showOnHomepage: quiz.showOnHomepage ?? true,
            });
            const sent = editableFingerprint(quiz);
            baseRef.current = sent;
            // Notre propre envoi change la date serveur (inconnue ici) : plus de date de départ,
            // donc pas d'avertissement « version plus récente » à cause de nous.
            baseUpdatedAtRef.current = null;
            setOutdated(false);
            // Modifié pendant l'enregistrement : on reste, le brouillon garde la suite.
            if (quizRef.current && editableFingerprint(quizRef.current) !== sent) {
                lastWrittenRef.current = null;
                flushDraftRef.current();
                return;
            }
            deleteDraft(clientID, quiz.id);
            lastWrittenRef.current = null;
            setSaved(true);
            // L'entrée « nouveau quiz » de l'historique porte une vieille copie du brouillon :
            // on la remplace par la page du quiz enregistré (Retour rouvre la version enregistrée).
            if (isNew) navigate(`/animator/${clientID}/quizzes/${quiz.id}`, { replace: true, state: { justSaved: true } });
            goToListTimerRef.current = setTimeout(() => navigate(`/animator/${clientID}/quizzes`), 700);
        } catch (e) {
            console.error('Quiz save failed:', e);
            Sentry.captureException(e, { tags: { area: 'quiz_studio_save' } });
            setSaveError(true);
        } finally {
            setSaving(false);
        }
    };

    const playable = quizIsPlayable(quiz);
    const issue = playable ? null : firstQuizIssue(quiz);
    const showIssue = (target: QuizIssue) => {
        if (target.kind === 'title') {
            document.getElementById('quiz-title')?.focus();
            return;
        }
        if (target.kind === 'noQuestion') {
            addQuestion();
            return;
        }
        setOpenId(target.questionId);
        requestAnimationFrame(() =>
            document
                .getElementById(`quiz-q-${target.questionId}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        );
    };
    const authorModes = quiz.questions.some((q) => q.mechanic === 'eclair' || q.mechanic === 'mise');
    let modeHint =
        'Automatique : le jeu place lui-même quelques Éclair (le premier qui buzze répond) et une Mise (on mise ses points avant de voir la question). Choisissez « Classique » pour l’éviter sur une question.';
    if (authorModes) {
        modeHint =
            'Vous avez placé vous-même un Éclair ou une Mise : le jeu n’en ajoute plus, les questions en « Automatique » restent classiques.';
    }
    if (quiz.scoringType === 'classic') {
        modeHint =
            'Éclair et La Mise demandent le barème « Vitesse » (leurs points fausseraient un barème à 1 point par bonne réponse).';
    }
    let saveLabel = 'Enregistrer le quiz';
    if (saved) saveLabel = 'Enregistré !';
    else if (saving) saveLabel = 'Enregistrement…';

    return (
        <div className={cockpit.page}>
            <div className={`${cockpit.shell} ${styles.editorShell}`}>
                <header className={cockpit.header}>
                    <button
                        type="button"
                        className={cockpit.back}
                        aria-label="Retour au studio"
                        onClick={() => navigate(`/animator/${clientID}/quizzes`)}
                    >
                        <LuChevronLeft />
                    </button>
                    <div className={cockpit.headTitles}>
                        <span className={cockpit.headKicker}>Studio Quiz</span>
                        <h1 className={cockpit.title}>{isNew ? 'Nouveau quiz' : 'Modifier le quiz'}</h1>
                    </div>
                </header>

                {restored && (
                    <div className={styles.draftBanner}>
                        {outdated
                            ? 'Brouillon récupéré, mais une version plus récente a été enregistrée ailleurs depuis : enregistrer ce brouillon la remplacera.'
                            : 'Brouillon récupéré : il n’est pas encore enregistré.'}
                        {outdated && savedVersion && (
                            <button
                                type="button"
                                className={styles.issueHint}
                                onClick={() => {
                                    if (clientID) deleteDraft(clientID, savedVersion.id);
                                    baseRef.current = editableFingerprint(savedVersion);
                                    lastWrittenRef.current = null;
                                    quizRef.current = savedVersion;
                                    setQuiz(savedVersion);
                                    setRestored(false);
                                    setOutdated(false);
                                }}
                            >
                                Prendre la version enregistrée
                            </button>
                        )}
                    </div>
                )}

                {draftState.fromAI && (
                    <div className={styles.aiBanner}>
                        <LuSparkles />
                        Quiz généré par IA — relisez les questions, corrigez ce qui doit l’être, puis enregistrez.
                    </div>
                )}

                {/* Méta du quiz */}
                <label className={styles.fieldLabel} htmlFor="quiz-title">
                    Titre du quiz
                </label>
                <input
                    id="quiz-title"
                    className={styles.textInput}
                    placeholder="Ex. « La spéciale années 80 »"
                    value={quiz.title}
                    onChange={(e) => patchQuiz({ title: e.target.value })}
                    maxLength={60}
                />

                <label className={styles.fieldLabel} htmlFor="quiz-description">
                    Description (optionnelle)
                </label>
                <textarea
                    id="quiz-description"
                    className={styles.textArea}
                    rows={2}
                    placeholder="Une phrase qui donne envie de jouer…"
                    value={quiz.description ?? ''}
                    onChange={(e) => patchQuiz({ description: e.target.value })}
                    maxLength={200}
                />

                <span className={styles.fieldLabel}>Catégorie</span>
                <div className={cockpit.btChips}>
                    {QUIZ_CATEGORIES.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            className={`${cockpit.btChip} ${quiz.category === c.id ? cockpit.btChipOn : ''}`}
                            onClick={() => patchQuiz({ category: quiz.category === c.id ? undefined : c.id })}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                <span className={styles.fieldLabel}>Image de couverture</span>
                <div className={styles.mediaRow}>
                    {quiz.coverImage ? (
                        <img className={styles.mediaPreview} src={quiz.coverImage} alt="Couverture du quiz" />
                    ) : (
                        <div className={styles.mediaPlaceholder}>Aucune image</div>
                    )}
                    <label className={`${cockpit.btChip} ${styles.mediaBtn}`}>
                        {uploading === 'cover' ? 'Envoi…' : quiz.coverImage ? 'Changer' : 'Ajouter une image'}
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            disabled={uploading !== null}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadImage('cover', 'cover-image', f, (url) => patchQuiz({ coverImage: url }));
                                e.target.value = '';
                            }}
                        />
                    </label>
                    {quiz.coverImage && (
                        <button
                            type="button"
                            className={styles.toolBtn}
                            onClick={() => patchQuiz({ coverImage: undefined })}
                        >
                            <LuTrash2 />
                        </button>
                    )}
                </div>
                {uploadErrorSlot === 'cover' && (
                    <p className={cockpit.ctaHint}>L’envoi de l’image a échoué. Réessayez.</p>
                )}

                <span className={styles.fieldLabel}>Mode de score</span>
                <div className={cockpit.btChips}>
                    <button
                        type="button"
                        className={`${cockpit.btChip} ${quiz.scoringType === 'speed' ? cockpit.btChipOn : ''}`}
                        onClick={() => patchQuiz({ scoringType: 'speed' })}
                    >
                        Vitesse (plus vite = plus de points)
                    </button>
                    <button
                        type="button"
                        className={`${cockpit.btChip} ${quiz.scoringType === 'classic' ? cockpit.btChipOn : ''}`}
                        onClick={() => patchQuiz({ scoringType: 'classic' })}
                    >
                        Classique (1 point par bonne réponse)
                    </button>
                </div>
                <div className={cockpit.btChips}>
                    <button
                        type="button"
                        className={`${cockpit.btChip} ${!quiz.lossEnabled ? cockpit.btChipOn : ''}`}
                        onClick={() => patchQuiz({ lossEnabled: false })}
                    >
                        Sans malus
                    </button>
                    <button
                        type="button"
                        className={`${cockpit.btChip} ${quiz.lossEnabled ? cockpit.btChipOn : ''}`}
                        onClick={() => patchQuiz({ lossEnabled: true })}
                    >
                        Malus si erreur
                    </button>
                </div>

                {/* Questions */}
                <h2 className={styles.sectionTitle}>Questions ({quiz.questions.length})</h2>
                {quiz.questions.map((q, index) => {
                    const open = openId === q.id;
                    const complete = questionIsComplete(q);
                    return (
                        <div
                            key={q.id}
                            id={`quiz-q-${q.id}`}
                            className={`${styles.questionCard} ${!complete ? styles.questionIncomplete : ''}`}
                        >
                            <button
                                type="button"
                                className={styles.questionHead}
                                onClick={() => setOpenId(open ? null : q.id)}
                            >
                                <span className={styles.questionIndex}>{index + 1}</span>
                                <span className={styles.questionPreview}>{q.question.trim() || 'Question vide…'}</span>
                                {(q.mechanic === 'eclair' || q.mechanic === 'mise') && (
                                    <span className={styles.modeBadge}>
                                        {q.mechanic === 'eclair' ? '⚡ Éclair' : '💰 La Mise'}
                                    </span>
                                )}
                                {!complete && <span className={styles.incompleteDot} title="Question incomplète" />}
                                {open ? <LuChevronUp /> : <LuChevronDown />}
                            </button>

                            {open && (
                                <div className={styles.questionBody}>
                                    <span className={styles.fieldLabel}>Type</span>
                                    <div className={cockpit.btChips}>
                                        {(
                                            [
                                                ['qcu', 'Choix unique'],
                                                ['qcm', 'Plusieurs réponses'],
                                                ['vf', 'Vrai / Faux'],
                                            ] as const
                                        ).map(([value, label]) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`${cockpit.btChip} ${q.type === value ? cockpit.btChipOn : ''}`}
                                                onClick={() => patchQuestion(q.id, coerceType(q, value))}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>

                                    <span className={styles.fieldLabel}>Mode de jeu</span>
                                    <div className={cockpit.btChips}>
                                        {MODES.map(([value, label]) => {
                                            const special = value === 'eclair' || value === 'mise';
                                            // Éclair = un seul joueur répond (pas de « plusieurs réponses ») ;
                                            // Éclair / Mise hors barème Vitesse : échelle de points faussée.
                                            const disabled =
                                                (special && quiz.scoringType === 'classic') ||
                                                (value === 'eclair' && q.type === 'qcm');
                                            return (
                                                <button
                                                    key={value ?? 'auto'}
                                                    type="button"
                                                    disabled={disabled}
                                                    className={`${cockpit.btChip} ${q.mechanic === value ? cockpit.btChipOn : ''}`}
                                                    onClick={() => patchQuestion(q.id, { mechanic: value })}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className={styles.modeHint}>{modeHint}</p>

                                    <label className={styles.fieldLabel} htmlFor={`q-${q.id}`}>
                                        La question
                                    </label>
                                    <textarea
                                        id={`q-${q.id}`}
                                        className={styles.textArea}
                                        rows={2}
                                        placeholder="Posez la question comme un animateur la lirait à voix haute…"
                                        value={q.question}
                                        onChange={(e) => patchQuestion(q.id, { question: e.target.value })}
                                        maxLength={160}
                                    />

                                    <span className={styles.fieldLabel}>
                                        Réponses — {q.type === 'qcm' ? 'cochez les bonnes' : 'cochez la bonne'}
                                    </span>
                                    {q.options.map((option, i) => {
                                        const isCorrect =
                                            q.type === 'qcm'
                                                ? (q.correctAnswers ?? []).includes(i)
                                                : q.correctAnswer === i;
                                        return (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <div key={i} className={styles.optionRow}>
                                                <button
                                                    type="button"
                                                    className={`${styles.optionMark} ${isCorrect ? styles.optionMarkOn : ''}`}
                                                    aria-label={`Marquer ${OPTION_LETTERS[i]} comme bonne réponse`}
                                                    onClick={() => toggleCorrect(q, i)}
                                                >
                                                    {isCorrect ? <LuCheck /> : OPTION_LETTERS[i]}
                                                </button>
                                                <input
                                                    className={styles.optionInput}
                                                    placeholder={`Réponse ${OPTION_LETTERS[i]}`}
                                                    value={option}
                                                    disabled={q.type === 'vf'}
                                                    maxLength={80}
                                                    onChange={(e) => {
                                                        const options = q.options.slice();
                                                        options[i] = e.target.value;
                                                        patchQuestion(q.id, { options });
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}

                                    <span className={styles.fieldLabel}>Temps pour répondre</span>
                                    <div className={cockpit.btChips}>
                                        {DURATIONS.map((d) => (
                                            <button
                                                key={d}
                                                type="button"
                                                className={`${cockpit.btChip} ${q.duration === d ? cockpit.btChipOn : ''}`}
                                                onClick={() => patchQuestion(q.id, { duration: d })}
                                            >
                                                {d}s
                                            </button>
                                        ))}
                                    </div>

                                    <label className={styles.fieldLabel} htmlFor={`a-${q.id}`}>
                                        Anecdote (lue par Max après la révélation)
                                    </label>
                                    <textarea
                                        id={`a-${q.id}`}
                                        className={styles.textArea}
                                        rows={2}
                                        placeholder="Le petit détail qui fait dire « ah ouais ?! » à la salle…"
                                        value={q.anecdote ?? ''}
                                        onChange={(e) => patchQuestion(q.id, { anecdote: e.target.value })}
                                        maxLength={220}
                                    />

                                    <span className={styles.fieldLabel}>Image (optionnelle)</span>
                                    <div className={styles.mediaRow}>
                                        {q.imageUrl ? (
                                            <img className={styles.mediaPreview} src={q.imageUrl} alt="" />
                                        ) : (
                                            <div className={styles.mediaPlaceholder}>Aucune image</div>
                                        )}
                                        <label className={`${cockpit.btChip} ${styles.mediaBtn}`}>
                                            {uploading === q.id ? 'Envoi…' : q.imageUrl ? 'Changer' : 'Ajouter'}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                hidden
                                                disabled={uploading !== null}
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (f)
                                                        uploadImage(q.id, `questions/${q.id}`, f, (url) =>
                                                            patchQuestion(q.id, { imageUrl: url })
                                                        );
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                        {q.imageUrl && (
                                            <button
                                                type="button"
                                                className={styles.toolBtn}
                                                onClick={() => patchQuestion(q.id, { imageUrl: undefined })}
                                                aria-label="Retirer l'image"
                                            >
                                                <LuTrash2 />
                                            </button>
                                        )}
                                    </div>
                                    {uploadErrorSlot === q.id && (
                                        <p className={cockpit.ctaHint}>L’envoi de l’image a échoué. Réessayez.</p>
                                    )}

                                    <div className={styles.questionTools}>
                                        <button
                                            type="button"
                                            className={styles.toolBtn}
                                            onClick={() => moveQuestion(index, -1)}
                                            disabled={index === 0}
                                            aria-label="Monter la question"
                                        >
                                            <LuArrowUp />
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.toolBtn}
                                            onClick={() => moveQuestion(index, 1)}
                                            disabled={index === quiz.questions.length - 1}
                                            aria-label="Descendre la question"
                                        >
                                            <LuArrowDown />
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.toolBtn} ${styles.toolDanger}`}
                                            onClick={() => removeQuestion(q.id)}
                                            disabled={quiz.questions.length <= 1}
                                            aria-label="Supprimer la question"
                                        >
                                            <LuTrash2 />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                <button type="button" className={styles.addGhost} onClick={addQuestion}>
                    <LuPlus /> Ajouter une question
                </button>

                {/* Enregistrement (barre collante en bas) */}
                <div className={styles.saveBar}>
                    <button type="button" className={cockpit.launchCta} onClick={save} disabled={!playable || saving}>
                        {saved ? <LuCheck /> : null}
                        {saveLabel}
                    </button>
                    {saveError && (
                        <p className={styles.saveError}>
                            {draftStored
                                ? 'L’enregistrement a échoué. Votre quiz est gardé dans ce navigateur : vérifiez la connexion et réessayez.'
                                : 'L’enregistrement a échoué, et ce navigateur ne peut pas garder le quiz : ne fermez pas cette page et réessayez.'}
                        </p>
                    )}
                    {issue && !saveError && (
                        <button type="button" className={styles.issueHint} onClick={() => showIssue(issue)}>
                            {quizIssueMessage(issue)}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
